import { FrameDTO } from '../../demo/dto/FrameDTO';
import { FrameTooltipDTO } from '../../demo/dto/FrameTooltipDTO';
import { GuideProgress } from '../helpers/guideStorageHelper';
import type { TriggerPeriodRecurrence } from '../helpers/customPeriodHelper';
import { TagLinkDTO } from '../../demo/dto/TagLinkDTO';

export enum PageTargetingRule {
  EQUALS = 'EQUALS',
  NOT_EQUALS = 'NOT_EQUALS',
  CONTAINS = 'CONTAINS',
  NOT_CONTAINS = 'NOT_CONTAINS',
  STARTS_WITH = 'STARTS_WITH',
  ENDS_WITH = 'ENDS_WITH',
  REGEX = 'REGEX'
}

export enum Trigger {
  ONLY_ONCE = 'ONLY_ONCE',
  ONCE_IN_SESSION = 'ONCE_IN_SESSION',
  ONCE_IN_1_DAY = 'ONCE_IN_1_DAY',
  ONCE_IN_7_DAYS = 'ONCE_IN_7_DAYS',
  ONCE_IN_30_DAYS = 'ONCE_IN_30_DAYS',
  ONCE_IN_90_DAYS = 'ONCE_IN_90_DAYS',
  ALWAYS = 'ALWAYS',
  MANUAL = 'MANUAL',
  CUSTOM_PERIOD = 'CUSTOM_PERIOD'
}

export enum GuideStatus {
  ACTIVE = 'ACTIVE',
  DEVELOPMENT = 'DEVELOPMENT',
  DISABLED = 'DISABLED'
}

export enum PageTargetingCondition {
  AND = 'AND',
  OR = 'OR'
}

export enum Priority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  VERY_HIGH = 'VERY_HIGH'
}

export interface GuideDTO {
  id: string;
  companyId: string;
  userId: string;
  pageTargeting: any;
  trigger: Trigger;
  frames: FrameDTO[];
  status: GuideStatus;
  name?: string;
  showStepsCounter: boolean;
  enableSkipButton: boolean;
  isVoiceoverEnabled: boolean;
  forceStart?: boolean;
  parent?: string;
  extensionVersion?: string;
  /** Um guia aceita várias etiquetas — diferente de demo e pesquisa, que têm `tagLink` único. */
  tagLinks?: TagLinkDTO[];
  callToAction?: any;
  openingCard?: any;
  createdAt: string;
  updatedAt: string;
  views?: number;
  isTourGuideEnabled?: boolean;
  steps: any[];
  audienceSegmentation?: {
    audienceType: 'all' | 'custom';
    segments?: Array<{ value: string }>;
    segmentsCondition?: 'AND' | 'OR';
  };
  segments?: Array<{ id: string; rules: any }>;
  segmentsCondition?: 'AND' | 'OR';
  type: 'GUIDE' | 'SURVEY' | 'POPUP';
  /**
   * Idioma em que o backend serviu este item. Só vem da rota pública — é ele que decide em que
   * idioma sai a MOLDURA do widget, para não misturar conteúdo traduzido com interface no
   * idioma do navegador do operador.
   */
  renderedLanguage?: string;
  availableLanguages?: string[];
  progress?: GuideProgress;
  priority?: Priority;
  closeCount?: number | null;
  userCloseCount?: number;
  stepResetAfterMinutes?: number | null;
  hideCloseOnFirstView?: boolean;
  hideOnComplete?: boolean;
  showUntilCompleted?: boolean;
  triggerPeriodStart?: string | null;
  triggerPeriodEnd?: string | null;
  triggerPeriodRecurring?: boolean;
  triggerPeriodRecurrence?: TriggerPeriodRecurrence | null;
  publishedAt?: string | null;
  publishedBy?: string | null;
  publishedChecksum?: string | null;
  unpublishedEdits?: boolean;
  version?: number;
  /**
   * How many active cards a frame of this guide may hold. Sent by GET /guide/:id and by the
   * PUT /guide/:id response; an older backend omits it, and then the editor uses 1 (today's
   * behavior).
   */
  cardsPerFrameLimit?: number;
  /** Company default tooltip styles — injected by GuideWrapper (not from GET guide). */
  defaultFrameTooltip?: FrameTooltipDTO;
}

export interface GuideDiffDTO {
  hasUnpublishedChanges: boolean;
  lastEditedAt: string;
  lastPublishedAt: string | null;
  fieldsChanged: string[];
}

export interface PublishResultDTO {
  noChanges: boolean;
  publishedAt: string;
  publishedBy: string | null;
  publishedChecksum: string;
  version: number;
  guide: GuideDTO;
}

export interface PublishConflictError {
  code: 'VERSION_CONFLICT';
  currentVersion: number;
  expectedVersion: number;
  message: string;
}

export type PublishStatus = 'never-published' | 'unpublished-changes' | 'synced';
